import { FavouritesEmptyState } from 'app/components/favourites-empty-state';
import { withErrorBoundary } from 'app/components/error-boundary';
import { ListingOfferCard } from 'app/components/listing-offer-card';
import { ListingOfferCardHorizontal } from 'app/components/listing-card-horizontal.large';
import { ListingRequestCard } from 'app/components/listing-request-card';
import { Tabs } from 'app/components/tabs';
import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useAllFavourites } from 'app/network/listing';
import type { Listing } from 'app/types/listing';
import { ListingType, TransactionMode } from 'app/types/listing';
import { getListingTransactionMode } from 'app/lib/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useUISize, useAlignmentIfDesktop, usePageTitle } from 'app/utils/web-helpers';
import { useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import type { NavigationScreen } from 'app/types/navigation';
import { ListingList } from 'app/components/listing-list';

type TypeFilter = 'all' | TransactionMode;

const FavouritesScreenComponent: NavigationScreen<'favourites'> = ({ navigation }) => {
  const isOffer = (listing: Listing): listing is Extract<Listing, { type: ListingType.offer }> =>
    listing.type === ListingType.offer;
  const isRequest = (listing: Listing): listing is Extract<Listing, { type: ListingType.request }> =>
    listing.type === ListingType.request;

  const { t } = useScopedTranslation();

  const setPageTitle = usePageTitle('favourites');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const {
    data: favouriteListings,
    isLoading,
    isRefetching,
    isPending,
    refetch,
  } = useAllFavourites({ throwOnError: true });

  const reversedListings = favouriteListings ? [...favouriteListings].reverse() : [];
  const [selectedTab, setSelectedTab] = useState<'offers' | 'requests'>('offers');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  const offerListings = reversedListings.filter(isOffer);
  const requestListings = reversedListings.filter(isRequest);
  const hasSelectedTabListings = selectedTab === 'offers' ? offerListings.length > 0 : requestListings.length > 0;

  const isHauteGaspesie = isWhiteLabel(WhiteLabel.HauteGaspesie);

  const lendCount = offerListings.filter((l) => getListingTransactionMode(l) === TransactionMode.lend).length;
  const sellCount = offerListings.filter((l) => getListingTransactionMode(l) === TransactionMode.sell).length;
  const giveCount = offerListings.filter((l) => getListingTransactionMode(l) === TransactionMode.give).length;

  const filteredOfferListings =
    typeFilter === 'all' ? offerListings : offerListings.filter((l) => getListingTransactionMode(l) === typeFilter);

  useRegisterLoading(isPending, 'FavouritesScreen');

  const alignment = useAlignmentIfDesktop();
  const { isLargeUI } = useUISize();

  const OfferCardComponent = isLargeUI ? ListingOfferCardHorizontal : ListingOfferCard;
  const listHeader = (
    <Column>
      <Text fontWeight="600" marginBottom="spacing.general.x8" textAlign={alignment} variant="headerXSmall">
        {t('title', { ns: 'favourites' })}
      </Text>
      <Tabs
        value={selectedTab}
        tabs={[
          { value: 'offers', label: t('tabs.offers', { ns: 'my-profile' }) },
          { value: 'requests', label: t('tabs.requests', { ns: 'my-profile' }) },
        ]}
        onPress={(tab) => {
          setSelectedTab(tab as 'offers' | 'requests');
          setTypeFilter('all');
        }}
      />
      {isHauteGaspesie && selectedTab === 'offers' ? (
        <Tabs
          value={typeFilter}
          tabs={[
            {
              value: 'all',
              label: t('filters.all', { ns: 'favourites' }),
              extraLabel: `(${String(offerListings.length)})`,
            },
            {
              value: TransactionMode.lend,
              label: t('mode.lend', { ns: 'all-listings' }),
              extraLabel: `(${String(lendCount)})`,
            },
            {
              value: TransactionMode.sell,
              label: t('mode.sell', { ns: 'all-listings' }),
              extraLabel: `(${String(sellCount)})`,
            },
            {
              value: TransactionMode.give,
              label: t('mode.give', { ns: 'all-listings' }),
              extraLabel: `(${String(giveCount)})`,
            },
          ]}
          onPress={(value) => setTypeFilter(value as TypeFilter)}
          variant="pill"
        />
      ) : null}
    </Column>
  );

  const emptyState = isLoading ? (
    <ActivityIndicator />
  ) : !hasSelectedTabListings ? (
    <FavouritesEmptyState
      buttonText={t('empty.primaryButtonText', { ns: 'favourites' })}
      onExplore={() =>
        navigation.navigate('billboard', {
          screen: 'all-listings',
          params: { type: selectedTab === 'offers' ? ListingType.offer : ListingType.request },
        })
      }
      subtitle={t('empty.secondLine', { ns: 'favourites' })}
      title={t('empty.firstLine', { ns: 'favourites' })}
    />
  ) : typeFilter !== 'all' ? (
    <Text color="text.tertiary" textAlign="center" variant="bodyBase">
      {t('empty.filtered', { ns: 'favourites' })}
    </Text>
  ) : (
    <Text color="text.tertiary" textAlign="center" variant="bodyBase">
      {t(`tabs.${selectedTab}`, { ns: 'my-profile' })}
    </Text>
  );

  return selectedTab === 'offers' ? (
    <ListingList
      ItemSeparatorComponent={() => <Column marginTop="spacing.general.x1" />}
      ListEmptyComponent={emptyState}
      ListHeaderComponent={listHeader}
      data={filteredOfferListings}
      onRefresh={() => void refetch()}
      refreshing={isRefetching}
      card={OfferCardComponent}
      listFormat={isLargeUI ? 'list' : 'grid'}
      keyExtractor={({ id, grouping }) => `${id}${grouping?.id ?? ''}`}
    />
  ) : (
    <ListingList
      ItemSeparatorComponent={() => <Column marginTop="spacing.general.x1" />}
      ListEmptyComponent={emptyState}
      ListHeaderComponent={listHeader}
      data={requestListings}
      onRefresh={() => void refetch()}
      refreshing={isRefetching}
      card={ListingRequestCard}
      listFormat="list"
      keyExtractor={({ id, grouping }) => `${id}${grouping?.id ?? ''}`}
    />
  );
};

export const FavouritesScreen: NavigationScreen<'favourites'> = withOptionalMetrics(
  'FavouritesScreen',
  withErrorBoundary(withDashboardLayout(FavouritesScreenComponent, 'simpleVerticalScrolling', true)),
);
